import React, { useState, useEffect, useCallback } from "react"
import MetaTags from "react-meta-tags"
import { Container, Modal, ModalBody, ModalHeader } from "reactstrap"
import { get } from "helpers/api_helper"
import moment from "moment"

/* ─── App primary color ──────────────────────────────────────── */
const PRIMARY = "#7e431b"
const PRIMARY_LIGHT = "#f5ece6"
const PRIMARY_DARK = "#5c3013"

/* ─── Stat colour map ────────────────────────────────────────── */
// NOTE: key must match the field name returned by the API (/api/tryprocess/live)
const STATS = [
  { key: "trys", label: "Trays", icon: "fas fa-clipboard-list", color: PRIMARY, bg: PRIMARY_LIGHT, status: "in" },
  { key: "assigned", label: "Assigned", icon: "fas fa-user-check", color: "#f59e0b", bg: "#fffbeb", status: "assigned" },
  { key: "completed", label: "Completed", icon: "fas fa-check-circle", color: "#16a34a", bg: "#f0fdf4", status: "completed" },
  { key: "trysOut", label: "Trays Out", icon: "fas fa-sign-out-alt", color: "#ef4444", bg: "#fef2f2", status: "out" },
]

/* ═══════════════════════════════════════════════════════════════
   DEPARTMENT CARD
═══════════════════════════════════════════════════════════════ */
const DepCard = ({ dep, stats, onStatClick }) => {
  const s = stats || {}
  return (
    <div style={card.wrap}>
      {/* Header */}
      <div style={card.header}>
        <span style={card.headerDot} />
        <span style={card.headerTitle}>{dep.name || `Dept ${dep.id}`}</span>
      </div>

      {/* 4 stat boxes — 2×2 grid */}
      <div style={card.grid}>
        {STATS.map(st => (
          <button
            key={st.key}
            style={{ ...card.stat, borderColor: s[st.key] > 0 ? st.color : "#e5e7eb" }}
            onClick={() => onStatClick(dep.id, st.status)}
            title={`View ${st.label}`}
          >
            <i className={st.icon} style={{ color: st.color, fontSize: 13, marginBottom: 3 }} />
            <span style={{ ...card.statNum, color: st.color }}>{s[st.key] ?? 0}</span>
            <span style={card.statLabel}>{st.label}</span>
          </button>
        ))}
      </div>

      {/* Repeated row */}
      <button
        style={card.repeated}
        onClick={() => onStatClick(dep.id, "repeat")}
        title="View Repeated Trays"
      >
        <i className="fas fa-redo-alt" style={{ color: "#3b82f6", fontSize: 11 }} />
        <span style={{ color: "#6b7280", fontSize: 11, fontWeight: 600 }}>Repeated Trays</span>
        <span style={{ color: "#3b82f6", fontWeight: 800, fontSize: 15, marginLeft: "auto" }}>
          {s.repeated ?? 0}
        </span>
      </button>
    </div>
  )
}

/* ─── Card styles ─────────────────────────────────────────────── */
const card = {
  wrap: {
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
    border: "1px solid #f0f0f0",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: PRIMARY,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  headerDot: {
    width: 6, height: 6, borderRadius: "50%",
    background: "rgba(255,255,255,0.6)", flexShrink: 0,
  },
  headerTitle: {
    color: "#fff", fontWeight: 700, fontSize: 12,
    letterSpacing: "0.3px", textTransform: "uppercase",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    padding: "8px 8px 4px",
    flex: 1,
  },
  stat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 4px",
    background: "#fafafa",
    border: "1.5px solid #e5e7eb",
    borderRadius: 7,
    cursor: "pointer",
    transition: "all 0.15s",
    gap: 1,
  },
  statNum: {
    fontSize: 20,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  repeated: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    background: "#f0f7ff",
    border: "none",
    borderTop: "1px solid #e5e7eb",
    cursor: "pointer",
    width: "100%",
    flexShrink: 0,
  },
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const ProcessLiveView = () => {
  const todayStr = () => moment().format("YYYY-MM-DD")

  const [departments, setDepartments] = useState([])
  const [depStats, setDepStats] = useState({})
  const [tryInfo, setTryInfo] = useState([])
  const [tryLabel, setTryLabel] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedDate, setSelectedDate] = useState(todayStr)

  const getCounts = useCallback(async (labCode, date) => {
    const { status, data } = await get(`/api/tryprocess/live?dep=${labCode}&date=${date}`)
    if (status) {
      setDepStats(prev => ({ ...prev, [labCode]: data }))
    }
  }, [])

  const fetchAll = useCallback(async (date) => {
    setRefreshing(true)
    setDepStats({})
    const d = date || selectedDate
    const { status, response } = await get(`/api/flow/config`)
    if (status) {
      const deps = JSON.parse(response?.config_data) || []
      setDepartments(deps)
      await Promise.all(deps.map(dep => getCounts(dep.id, d)))
    }
    setLastRefresh(new Date())
    setRefreshing(false)
  }, [getCounts, selectedDate])

  useEffect(() => {
    fetchAll(selectedDate)
    const interval = setInterval(() => fetchAll(selectedDate), 60000)
    return () => clearInterval(interval)
  }, [selectedDate])

  const handleStatClick = async (depId, status) => {
    const dep = departments.find(d => d.id === depId)
    const labelMap = { in: "Trays In", assigned: "Assigned", completed: "Completed", out: "Trays Out", repeat: "Repeated Trays" }
    setTryLabel(`${dep?.name || depId} — ${labelMap[status] || status}`)
    setTryInfo([])
    setModalOpen(true)
    setModalLoading(true)
    const { status: ok, data } = await get(`/api/tryprocess/trybystatus?dep=${depId}&status=${status}&date=${selectedDate}`)
    setModalLoading(false)
    if (ok) setTryInfo(Array.isArray(data) ? data : [])
  }

  const closeModal = () => {
    setModalOpen(false)
    setTryInfo([])
    setTryLabel("")
  }

  const downloadCSV = data => {
    const headers = ["#", "Tray No", "Case No", "Dept In", "Technician", "Assigned In", "Assigned Out", "Tech Duration", "Dept Out", "Dept Duration"]
    const rows = data.map((v, i) => [
      i + 1, v.try_no, v.case_no,
      v.department_in_at ? moment(v.department_in_at).format("DD-MM-YYYY HH:mm:ss") : "---",
      v.firstname ? `${v.firstname} ${v.lastname}` : "---",
      v.assigned_in_at ? moment(v.assigned_in_at).format("DD-MM-YYYY HH:mm:ss") : "---",
      v.assigned_out_at ? moment(v.assigned_out_at).format("DD-MM-YYYY HH:mm:ss") : "---",
      v.employee_duration || "---",
      v.department_out_at ? moment(v.department_out_at).format("DD-MM-YYYY HH:mm:ss") : "---",
      v.department_duration || "---",
    ])
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = "try_info.csv"; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <React.Fragment>
      <MetaTags><title>Live Dashboard | Process</title></MetaTags>

      <style>{`
        .live-page { padding: 16px 20px; min-height: 100vh; background: #f4f6f8; }

        /* page header */
        .live-topbar { display:flex; align-items:center; justify-content:space-between;
          margin-bottom:16px; flex-wrap:wrap; gap:8px; }
        .live-title { font-size:18px; font-weight:800; color:#1f2937; margin:0;
          display:flex; align-items:center; gap:8px; }
        .live-title-dot { width:10px; height:10px; border-radius:50%; background:${PRIMARY}; flex-shrink:0; }
        .live-meta { font-size:11px; color:#9ca3af; }
        .live-refresh-btn { padding:6px 14px; background:${PRIMARY}; color:#fff;
          border:none; border-radius:6px; font-size:12px; font-weight:700; cursor:pointer;
          display:flex; align-items:center; gap:6px; transition:opacity .2s; }
        .live-refresh-btn:hover { opacity:.85; }
        .live-refresh-btn:disabled { opacity:.5; cursor:not-allowed; }

        /* grid */
        .live-grid { display:grid;
          grid-template-columns: repeat(4, 1fr);
          gap:12px; }
        @media (max-width:1200px) { .live-grid { grid-template-columns: repeat(3,1fr); } }
        @media (max-width:900px)  { .live-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width:576px)  { .live-grid { grid-template-columns: 1fr; } }

        /* card hover */
        .live-dep-card:hover .dep-stat-box { background:#f9fafb; }
        .dep-stat-box:hover { transform:scale(1.03); box-shadow:0 2px 8px rgba(0,0,0,0.1) !important; }

        /* modal table */
        .live-tbl { width:100%; border-collapse:collapse; font-size:12px; }
        .live-tbl th { background:${PRIMARY}; color:#fff; padding:8px 10px;
          font-weight:700; white-space:nowrap; font-size:11px; letter-spacing:.3px; position:sticky; top:0; z-index:1; }
        .live-tbl td { padding:7px 10px; border-bottom:1px solid #f3f4f6; color:#374151; vertical-align:middle; }
        .live-tbl tr:hover td { background:#fdf8f5; }
        .live-tbl tr:last-child td { border-bottom:none; }
        .try-badge { display:inline-block; padding:2px 7px; border-radius:20px;
          font-size:10px; font-weight:700; background:${PRIMARY_LIGHT}; color:${PRIMARY}; }

        /* white close button on primary header */
        .modal-header .btn-close { filter:invert(1) brightness(2); opacity:.85; }
        .modal-header .btn-close:hover { opacity:1; }
        .modal-header .close { color:#fff !important; opacity:.85; text-shadow:none; }
        .modal-header .close:hover { opacity:1; }
      `}</style>

      <div className="page-content">
        <div className="live-page">

          {/* ── Top bar ── */}
          <div className="live-topbar">
            <div>
              <h3 className="live-title">
                <span className="live-title-dot" />
                Live Dashboard
                {/* Date badge */}
                <span style={{
                  marginLeft: 10, fontSize: 11, fontWeight: 600,
                  background: selectedDate === todayStr() ? "#dcfce7" : "#fef9c3",
                  color: selectedDate === todayStr() ? "#16a34a" : "#854d0e",
                  padding: "2px 10px", borderRadius: 20,
                }}>
                  {selectedDate === todayStr() ? "Today" : moment(selectedDate).format("DD MMM YYYY")}
                </span>
              </h3>
              {lastRefresh && (
                <div className="live-meta" style={{ marginTop: 2 }}>
                  Last updated: {moment(lastRefresh).format("hh:mm:ss A")} · Auto-refreshes every 60s
                </div>
              )}
            </div>

            {/* Controls */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {/* Date picker */}
              <input
                type="date"
                value={selectedDate}
                max={todayStr()}
                onChange={e => setSelectedDate(e.target.value)}
                style={{
                  padding: "6px 10px", border: `1.5px solid ${PRIMARY}`, borderRadius: 6,
                  fontSize: 12, color: "#374151", cursor: "pointer", outline: "none",
                  background: "#fff",
                }}
              />
              {/* Today shortcut */}
              {selectedDate !== todayStr() && (
                <button
                  style={{
                    padding: "6px 12px", background: "#f0fdf4", color: "#16a34a",
                    border: "1px solid #bbf7d0", borderRadius: 6, fontSize: 12,
                    fontWeight: 700, cursor: "pointer",
                  }}
                  onClick={() => setSelectedDate(todayStr())}
                >
                  Today
                </button>
              )}
              {/* Refresh */}
              <button
                className="live-refresh-btn"
                onClick={() => fetchAll(selectedDate)}
                disabled={refreshing}
              >
                <i className={`fas fa-sync-alt ${refreshing ? "fa-spin" : ""}`} />
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>

          </div>

          {/* ── Department grid ── */}
          {departments.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
              <i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: PRIMARY }} />
              <p style={{ marginTop: 12 }}>Loading departments…</p>
            </div>
          ) : (
            <div className="live-grid">
              {departments.map((dep, i) => (
                <div key={`DEP_${i}`} className="live-dep-card">
                  <DepCard
                    dep={dep}
                    stats={depStats[dep.id]}
                    onStatClick={handleStatClick}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tray Info Modal ── */}
      <Modal isOpen={modalOpen} size="xl" toggle={closeModal} centered>
        <ModalHeader
          toggle={closeModal}
          style={{ background: PRIMARY, color: "#fff", borderBottom: "none", padding: "12px 18px" }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff" }}>
            <i className="fas fa-clipboard-list" />
            <span style={{ fontWeight: 700, fontSize: 14 }}>{tryLabel}</span>
            {!modalLoading && (
              <span style={{
                background: "rgba(255,255,255,0.2)", borderRadius: 20,
                padding: "2px 10px", fontSize: 11, fontWeight: 600,
                color: "#fff"
              }}>
                {tryInfo.length} tray{tryInfo.length !== 1 ? "s" : ""}
              </span>
            )}
          </span>
        </ModalHeader>

        <ModalBody style={{ padding: "14px 16px" }}>
          {/* Loading state */}
          {modalLoading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: PRIMARY }} />
              <p style={{ marginTop: 10, color: "#9ca3af", fontSize: 13 }}>Loading trays…</p>
            </div>
          )}

          {/* Empty state */}
          {!modalLoading && tryInfo.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <i className="fas fa-inbox fa-2x" style={{ color: "#d1d5db" }} />
              <p style={{ marginTop: 10, color: "#9ca3af", fontSize: 13 }}>No trays found for this selection.</p>
            </div>
          )}

          {/* Table */}
          {!modalLoading && tryInfo.length > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <button
                  onClick={() => downloadCSV(tryInfo)}
                  style={{
                    padding: "6px 14px", background: "#16a34a", color: "#fff",
                    border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <i className="fas fa-download" /> Download CSV
                </button>
              </div>
              <div style={{ maxHeight: 450, overflowY: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <table className="live-tbl">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Tray No</th>
                      <th>Case No</th>
                      <th>Dept In At</th>
                      <th>Technician</th>
                      <th>Assigned In</th>
                      <th>Assigned Out</th>
                      <th>Tech Duration</th>
                      <th>Dept Out At</th>
                      <th>Dept Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tryInfo.map((v, i) => (
                      <tr key={i}>
                        <td style={{ color: "#9ca3af", fontSize: 11 }}>{i + 1}</td>
                        <td><span className="try-badge">{v.try_no}</span></td>
                        <td><strong style={{ color: PRIMARY }}>{v.case_no}</strong></td>
                        <td>{v.department_in_at ? moment(v.department_in_at).format("DD-MM-YY HH:mm") : "—"}</td>
                        <td>{v.firstname ? `${v.firstname} ${v.lastname || ""}` : "—"}</td>
                        <td>{v.assigned_in_at ? moment(v.assigned_in_at).format("DD-MM-YY HH:mm") : "—"}</td>
                        <td>{v.assigned_out_at ? moment(v.assigned_out_at).format("DD-MM-YY HH:mm") : "—"}</td>
                        <td>{v.employee_duration || "—"}</td>
                        <td>{v.department_out_at ? moment(v.department_out_at).format("DD-MM-YY HH:mm") : "—"}</td>
                        <td>{v.department_duration || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>


    </React.Fragment>
  )
}

export default ProcessLiveView
